import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { demeterCalculatorsApi } from '../../../../Apis/Apis';
import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import { DeleteCalculationEngineInputsRequest, DemeterCalculatorType } from '../../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../../Layouts/NavigationRoutes';
import useCacheThenApi from '../../../Apis/Hooks/useCacheThenApiHook';
import useMultipleApis from '../../../Apis/Hooks/useMultipleApisHook';
import PageHeaderWithBackNavigation from '../../../Components/Headers/PageHeaderWithBackNavigation';
import PaginationTableWrapper from '../../../Components/Tables/TableWrapper/PaginationTableWrapper';
import CacheKeys from '../../../Services/Cache/CacheKeys';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from './ValorizationCalculator.module.scss';

const CalculatorsValorizationListPage: React.FC = () => {
    // Application hooks.
    const [translations, translate] = useLanguage();
    const navigate = useNavigate();

    // Api hooks.
    const [, refreshListCalculationEngineInputsByGroup, listCalculationEngineInputsByGroupResponse] = useCacheThenApi(
        CacheKeys.ListCalculationEngineInputsByGroup,
        () => demeterCalculatorsApi.listCalculationEngineInputsByGroup(DemeterCalculatorType.Valorization),
    );

    const [, refreshDeleteCalculationEngine, deleteCalculationEngineResponse] = useMultipleApis(
        (requests?: DeleteCalculationEngineInputsRequest[]) =>
            requests?.map((x) => demeterCalculatorsApi.deleteCalculationEngineInputs(x!.demeterCalculationEngineGuid, x!.demeterCalculationEngineInputsGuid)),
        { stopAutoExecute: true, successMessage: translations.calculators.valorization.messages.deleteCalculationEngineInputsSuccessful },
    );

    useEffect(() => {
        if (!deleteCalculationEngineResponse) {
            return;
        }

        refreshListCalculationEngineInputsByGroup();
    }, [deleteCalculationEngineResponse]);

    const valorizationModelsOptions = useMemo(() => {
        if (!listCalculationEngineInputsByGroupResponse) {
            return [];
        }

        const options = [
            {
                id: 'default',
                modelName: (
                    <div>
                        {translations.words.standard} ({translations.words.default})
                    </div>
                ),
                savedBy: translations.words.system,
                lastUpdated: applicationConstants.TablePlaceholderZeroOrEmpty,
                actions: <div>{translations.words.edit}</div>,
            },
        ];

        listCalculationEngineInputsByGroupResponse.rows!.forEach((x) => {
            options.push({
                id: x.groupName,
                modelName: <Link to={`${NavigationRoutes.Calculators}?tab=Valorization&groupName=${x.groupName}`}>{translate(x.groupName)}</Link>,
                savedBy: x.modifiedBy ?? applicationConstants.TablePlaceholderZeroOrEmpty,
                lastUpdated: x.modifiedAt ? formattingService.toShortDayMonthYear(new Date(x.modifiedAt)) : applicationConstants.TablePlaceholderZeroOrEmpty,
                actions: (
                    <>
                        <Link to={`${NavigationRoutes.Calculators}?tab=Valorization&groupName=${x.groupName}`}>{translations.words.edit}</Link>
                        <div className={styles.valorization_list_actions_spacer}>{' | '}</div>
                        <button
                            type="button"
                            onClick={() =>
                                refreshDeleteCalculationEngine(
                                    x.calculationEngineInputs.map((y) => ({
                                        demeterCalculationEngineGuid: y.demeterCalculationEngineGuid,
                                        demeterCalculationEngineInputsGuid: y.demeterCalculationEngineInputsGuid,
                                    })),
                                )
                            }
                        >
                            {translations.actions.delete}
                        </button>
                    </>
                ),
            });
        });

        return options;
    }, [translations, listCalculationEngineInputsByGroupResponse]);

    const listCalculationEngineInputsByGroupResponseWithDefaultModel = useMemo(
        () => ({
            ...listCalculationEngineInputsByGroupResponse,
            totalCount: (listCalculationEngineInputsByGroupResponse?.totalCount ?? 0) + 1,
            take: applicationConstants.MaximumTake,
        }),
        [listCalculationEngineInputsByGroupResponse],
    );

    // TODO: add pagination.
    const handlePagination = () => {};

    const handleTakeCountUpdate = () => {};

    const navigateBackToValorization = () => {
        navigate(-1);
    };

    return (
        <div className={styles.master_page_container}>
            <PageHeaderWithBackNavigation
                handleBackNavigation={navigateBackToValorization}
                title={translations.calculators.valorization.text.valorizationModels}
                track={{ eventCategory: 'ValorizationCalculator', eventAction: 'BackClicked' }}
            />
            <div className={styles.valorization_list_table}>
                <PaginationTableWrapper
                    apiListResponse={listCalculationEngineInputsByGroupResponseWithDefaultModel}
                    showPagination
                    handlePagination={handlePagination}
                    handleTakeCountUpdate={handleTakeCountUpdate}
                    track={{
                        eventCategory: 'ValorizationCalculator',
                        eventAction: 'PaginationChanged',
                    }}
                >
                    <>
                        <div className={styles.valorization_list_row}>
                            <div className={styles.valorization_list_header_cell}>{translations.calculators.valorization.text.modelName}</div>
                            <div className={styles.valorization_list_header_cell}>{translations.text.savedBy}</div>
                            <div className={styles.valorization_list_header_cell}>{translations.text.lastUpdated}</div>
                            <div className={styles.valorization_list_header_cell}>{translations.words.actions}</div>
                        </div>

                        {valorizationModelsOptions.map((x) => (
                            <div key={x.id} className={styles.valorization_list_row}>
                                <div className={styles.valorization_list_model_name}>{x.modelName}</div>
                                <div className={styles.valorization_list_saved_by}>{x.savedBy}</div>
                                <div className={styles.valorization_list_last_updated}>{x.lastUpdated}</div>
                                <div className={styles.valorization_list_actions}>{x.actions}</div>
                            </div>
                        ))}
                    </>
                </PaginationTableWrapper>
            </div>
        </div>
    );
};

export default CalculatorsValorizationListPage;
